import { auth, db } from "../config/firebase";
import { Link } from "@beyondrealityapp/core/link/classes";
import { LinkField, LinkTargetModel } from "@beyondrealityapp/core/link/constants";
import { LinkDocument } from "@beyondrealityapp/core/link/types";
import { GoalField, GoalStatus } from "@beyondrealityapp/core/goal/constants";
import { Model, Operator } from "@beyondrealityapp/core/shared/constants";
import { LinkTarget } from "../utils/recordLinks";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { collection, getDocs, query, where } from "firebase/firestore";

type FetchLinksArgs = {
  targetModel: LinkTargetModel;
  targetId: string;
};

/**
 * Links are written by Cloud Functions after a source record is saved, so
 * they are read-only here.
 */
export const linksApi = createApi({
  reducerPath: "linksApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Link", "LinkTarget"],
  endpoints: (builder) => ({
    fetchLinksToRecord: builder.query<Link[], FetchLinksArgs>({
      queryFn: async ({ targetModel, targetId }) => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { data: [] };
        try {
          const links = await _fetchLinksToRecord(uid, targetModel, targetId);
          return { data: links };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: "Link", id: `${arg.targetModel}-${arg.targetId}` },
      ],
    }),
    fetchLinkTargets: builder.query<LinkTarget[], void>({
      queryFn: async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { data: [] };
        try {
          const targets = await _fetchLinkTargets(uid);
          return { data: targets };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["LinkTarget"],
    }),
  }),
});

export const { useFetchLinksToRecordQuery, useFetchLinkTargetsQuery } = linksApi;

/** Links the user can see to a record, newest first. */
export const _fetchLinksToRecord = async (
  uid: string,
  targetModel: LinkTargetModel,
  targetId: string
): Promise<Link[]> => {
  const q = query(
    collection(db, Model.LINKS),
    where(LinkField.TARGET_MODEL, Operator.EQUAL_TO, targetModel),
    where(LinkField.TARGET_ID, Operator.EQUAL_TO, targetId),
    where(LinkField.SHARED_WITH_UIDS, Operator.ARRAY_CONTAINS, uid)
  );
  const snapshot = await getDocs(q);
  // Sorted here rather than in the query, so no composite index is needed
  return snapshot.docs
    .map((doc) => Link.fromFirestore(doc.data() as LinkDocument))
    .sort(
      (a, b) =>
        _toTime(b.metaData.createdAt) - _toTime(a.metaData.createdAt)
    );
};

/** The user's own records that can be linked to from rich text. */
export const _fetchLinkTargets = async (uid: string): Promise<LinkTarget[]> => {
  const q = query(
    collection(db, Model.GOALS),
    where(GoalField.UID, Operator.EQUAL_TO, uid)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      model: Model.GOALS,
      id: doc.id,
      label: typeof data.content === "string" ? data.content : "",
      emoji: typeof data.emoji === "string" ? data.emoji : "",
      status: data.status as GoalStatus,
      updatedAt: _toTime(data.metaData?.updatedAt?.toDate?.()),
    };
  });
};

const _toTime = (date: unknown): number =>
  date instanceof Date ? date.getTime() : 0;
